import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { api } from "../lib/http";
import { useLanguage } from "./LanguageContext";

const API_BASE_URL = "https://shopaholics.ge/api";

/* ======== THEME ======== */
const PALETTE = {
  bg: "rgba(0,0,0,.55)",
  card: "#ffffff",
  ink: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.10)",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  successBg: "#e8f5e9",
  successInk: "#2e7d32",
  successLine: "#c8e6c9",
  danger: "#b00020",
  ring: "rgba(161, 90, 90, .35)",
  subtle: "#f7f7f8",
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(.98) }
  to   { opacity: 1; transform: translateY(0)   scale(1) }
`;

const Overlay = styled.div`
  position: fixed; inset: 0;
  background: ${PALETTE.bg};
  backdrop-filter: blur(3px);
  z-index: 3001;
  display: flex; justify-content: center; align-items: center;
  padding: 16px;
`;

const Popup = styled.div`
  position: relative;
  width: min(560px, 96vw);
  background: ${PALETTE.card};
  color: ${PALETTE.ink};
  border: 1px solid ${PALETTE.line};
  border-radius: 18px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  animation: ${fadeIn} 160ms ease-out;
`;

const ScrollArea = styled.div`
  max-height: min(90vh, 720px);
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { width: 0; height: 0; background: transparent; }

  overscroll-behavior: contain;
  scroll-behavior: smooth;
`;

const Form = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 6px;
`;

const Field = styled.label`
  display: grid;
  gap: 6px;
  width: 100%;
  min-width: 0;
`;

const BaseInput = `
  width: 100%;
  min-width: 0;
  padding: 12px;
  border: 1px solid ${PALETTE.line};
  background: ${PALETTE.card};
  color: ${PALETTE.ink};
  border-radius: 12px;
  font-size: 16px;
  line-height: 1.25;
  transition: border-color .15s ease, box-shadow .15s ease, background .15s;
  outline: none;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
  &::placeholder { color: #9aa1ab; }
  &:focus {
    border-color: ${PALETTE.brand};
    box-shadow: 0 0 0 4px ${PALETTE.ring};
  }
`;

const Input = styled.input`${BaseInput}`;
const Textarea = styled.textarea`
  ${BaseInput}
  min-height: 92px;
  resize: vertical;
  max-width: 100%;
`;

const Select = styled.select`
  ${BaseInput}
  background-image:
    linear-gradient(45deg, transparent 50%, #777 50%),
    linear-gradient(135deg, #777 50%, transparent 50%),
    linear-gradient(to right, transparent, transparent);
  background-position:
    calc(100% - 18px) calc(50% - 3px),
    calc(100% - 12px) calc(50% - 3px),
    100% 0;
  background-size: 6px 6px, 6px 6px, 2.5em 100%;
  background-repeat: no-repeat;
  padding-right: 40px;
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: 8px;
  padding: 6px 6px 12px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 900;
  letter-spacing: 0.2px;
`;

const Subtitle = styled.div`
  font-size: 13px;
  color: ${PALETTE.dim};
`;

const CloseBtn = styled.button`
  border: 1px solid ${PALETTE.line};
  background: ${PALETTE.card};
  color: ${PALETTE.dim};
  width: 34px; height: 34px;
  border-radius: 10px;
  display: grid; place-items: center;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s;
  &:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(0,0,0,.08); }
  &:active { transform: translateY(0); }
`;

const Lab = styled.span`
  font-size: 12px;
  font-weight: 800;
  color: ${PALETTE.dim};
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const ErrorText = styled.div`
  color: ${PALETTE.danger};
  font-size: 12px;
  margin-top: -4px;
`;

const Hint = styled.small`
  display: block;
  color: ${PALETTE.dim};
  margin-top: 2px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${PALETTE.successBg};
  color: ${PALETTE.successInk};
  border: 1px solid ${PALETTE.successLine};
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 800;
`;

const Buttons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 10px;
`;

const Button = styled.button`
  --bg: ${PALETTE.brand};
  --ink: #fff;
  --b: ${PALETTE.brandDark};

  appearance: none;
  border: 1px solid var(--b);
  background: var(--bg);
  color: var(--ink);
  padding: 11px 16px;
  border-radius: 12px;
  font-weight: 900;
  font-size: 14px;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease, background 0.12s,
    border-color 0.12s;

  &:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(161, 90, 90, 0.22); }
  &:active { transform: translateY(0); }

  ${({ variant }) =>
    variant === "ghost" &&
    `
      --bg: ${PALETTE.subtle};
      --ink: ${PALETTE.ink};
      --b: ${PALETTE.line};
    `}
  ${({ variant }) =>
    variant === "cancel" &&
    `
      --bg: #f1f2f4;
      --ink: #444;
      --b: ${PALETTE.line};
    `}

  &:disabled { opacity: .6; cursor: not-allowed; transform: none; box-shadow: none; }
`;

const PhoneRow = styled.div`
  display: grid;
  grid-template-columns: 190px 1fr;
  gap: 10px;
  width: 100%;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const PayBox = styled.div`
  margin-top: 10px;
  padding: 12px;
  border: 1px solid ${PALETTE.line};
  background: ${PALETTE.subtle};
  border-radius: 14px;
  display: grid;
  gap: 10px;
`;

const PayTitle = styled.div`
  font-weight: 900;
  font-size: 13px;
  color: ${PALETTE.ink};
`;

const CheckRow = styled.label`
  display: flex;
  gap: 10px;
  align-items: center;
  cursor: pointer;
  user-select: none;
  font-size: 13px;
  color: ${PALETTE.ink};
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${PALETTE.brand};
`;

const SmallMuted = styled.div`
  font-size: 12px;
  color: ${PALETTE.dim};
  line-height: 1.35;
`;

const WarningHint = styled(Hint)`
  color: #b45309;
  font-weight: 600;
`;

const CityDropdown = styled.div`
  position: relative;
`;

const CityButton = styled.button`
  ${BaseInput}
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CityList = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 5000;
  background: #fff;
  border: 1px solid ${PALETTE.line};
  border-radius: 14px;
  box-shadow: 0 16px 36px rgba(0,0,0,.14);
  overflow: hidden;
`;

const CityOption = styled.button`
  width: 100%;
  padding: 13px 14px;
  border: 0;
  background: #fff;
  text-align: left;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    background: ${PALETTE.subtle};
  }
`;
/* ======== COUNTRIES ======== */
const COUNTRIES = [{ iso: "GE", name: "Georgia (+995)", dial: "995", min: 9, max: 9 }];

export default function AddressPopup({
  onClose,
  onSubmit,
  defaultCountryIso = "GE",
  knownUserId = null,
  assumeHasPhone = false,
}) {
  const { t, language } = useLanguage();
  const lang = language === "ka" ? "ka" : "en";

  // helpers for bilingual fallback
  const TT = (key, kaFallback, enFallback) => {
    const v = t?.(key);
    if (typeof v === "string" && v.trim()) return v;
    return lang === "ka" ? kaFallback : enFallback;
  };

  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [comment, setComment] = useState("");

  const [phoneIso, setPhoneIso] = useState(defaultCountryIso);
  const [phone, setPhone] = useState("");
  const [phoneErr, setPhoneErr] = useState("");

  const [requirePhone, setRequirePhone] = useState(assumeHasPhone ? false : null);
  const [resolvedUserId, setResolvedUserId] = useState(knownUserId);

  const [otpVisible, setOtpVisible] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpMsg, setOtpMsg] = useState("");

  const country = useMemo(
    () => COUNTRIES.find((c) => c.iso === phoneIso) || COUNTRIES[0],
    [phoneIso]
  );

  // cards
  const [saveCard, setSaveCard] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCardToken, setSelectedCardToken] = useState("");
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsErr, setCardsErr] = useState("");

  // PAY LOCK
  const [paying, setPaying] = useState(false);


  useEffect(() => {
    let canceled = false;

    const loadCards = async () => {
      setCardsErr("");
      setCardsLoading(true);
      try {
        const r = await api.get(`${API_BASE_URL}/get_saved_cards.php`, {
          withCredentials: true,
          validateStatus: () => true,
        });

        if (canceled) return;

        if (r.data?.success) {
          const raw = Array.isArray(r.data.cards) ? r.data.cards : [];
          const items = raw.map((c) => {
            const token =
              c.token || c.payment_method_token || c.pm_token || c.token_id || c.id || "";
            return { ...c, token: String(token) };
          });

          setSavedCards(items);

          const def = items.find((x) => String(x.is_default) === "1" || x.is_default === 1);
          if (def?.token) setSelectedCardToken(def.token);
        } else if (r.status === 401) {
          setSavedCards([]);
        } else {
          setSavedCards([]);
          setCardsErr(r.data?.error || TT("payment.cards_load_failed", "ბარათები ვერ ჩაიტვირთა", "Failed to load cards"));
        }
      } catch {
        if (!canceled) setCardsErr(TT("payment.network_cards", "ქსელური შეცდომა (ბარათები)", "Network error (cards)"));
      } finally {
        if (!canceled) setCardsLoading(false);
      }
    };

    loadCards();
    return () => { canceled = true; };
  }, []);

  // Timer
  useEffect(() => {
    if (otpTimer <= 0) return;
    const id = setInterval(() => setOtpTimer((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [otpTimer]);

  // Reset OTP on phone change
  useEffect(() => {
    setOtpVisible(false);
    setOtpVerified(false);
    setOtpCode("");
    setOtpMsg("");
    setOtpTimer(0);
  }, [phone, phoneIso]);

  useEffect(() => {
    if (assumeHasPhone) setRequirePhone(false);
  }, [assumeHasPhone, knownUserId]);

  useEffect(() => {
    let canceled = false;

    const decide = async (doRetry = true) => {
      if (assumeHasPhone) {
        setRequirePhone(false);
        return;
      }
      try {
        const res = await api.get(`${API_BASE_URL}/me.php`, { withCredentials: true });
        const me = res.data || {};
        const usr = me.user || {};
        const id = knownUserId || usr.id || usr.user_id || null;
        if (!canceled) setResolvedUserId(id);

        const hasPhone = !!me.has_phone;
        if (!canceled) setRequirePhone(!hasPhone);

        if (doRetry && (!me.auth || !hasPhone)) {
          setTimeout(() => { if (!canceled) decide(false); }, 500);
        }
      } catch {
        if (!canceled) setRequirePhone(null);
      }
    };

    decide(true);
    return () => { canceled = true; };
  }, [knownUserId, assumeHasPhone]);

  const normalizeDigits = (v) => String(v || "").replace(/\D+/g, "");
  const validLength = (digits) => digits.length >= country.min && digits.length <= country.max;
  const validByCountry = (digits) => {
    if (!validLength(digits)) return false;
    if (country.iso === "GE") return digits.length === 9 && digits.startsWith("5");
    return true;
  };
  const phoneE164 = () => `+${country.dial}${normalizeDigits(phone)}`;

  const sendOtp = async () => {
    const digits = normalizeDigits(phone);
    setPhoneErr("");
    setOtpMsg("");

    if (!digits) {
      setPhoneErr(TT("address.phone_required", "ტელეფონის ნომერი სავალდებულოა", "Phone is required"));
      return;
    }
    if (!validByCountry(digits)) {
      setPhoneErr(
        (TT("address.phone_invalid_range", "ტელეფონის ფორმატი არასწორია", "Invalid phone format")) +
          ` (${country.min}-${country.max})`
      );
      return;
    }

    setOtpSending(true);
    try {
      const r = await api.post(
        `${API_BASE_URL}/auth/request_sms_code.php`,
        { phone: phoneE164() },
        { headers: { "Content-Type": "application/json" }, validateStatus: () => true }
      );

      if (r.data?.ok) {
        setOtpVisible(true);
        setOtpTimer(60);
        setOtpMsg(TT("address.code_sent", "კოდი გამოიგზავნა", "Code sent"));
      } else {
        setOtpVisible(true);
        setOtpMsg(r.data?.msg || r.data?.debug?.response || TT("address.send_failed", "ვერ გაიგზავნა", "Failed to send"));
      }
    } catch {
      setOtpVisible(true);
      setOtpMsg(TT("address.network_error", "ქსელური შეცდომა. სცადეთ ხელახლა.", "Network error. Try again."));
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    if (otpCode.length !== 6) return;
    setOtpSending(true);
    setOtpMsg("");

    try {
      const v = await api.post(
        `${API_BASE_URL}/auth/verify_sms_code.php`,
        { phone: phoneE164(), code: otpCode },
        { headers: { "Content-Type": "application/json" }, validateStatus: () => true }
      );

      if (v.data?.ok) {
        setOtpVerified(true);
        setOtpMsg(TT("address.verified", "დადასტურებულია", "Verified"));
      } else {
        setOtpVerified(false);
        setOtpMsg(v.data?.msg || TT("address.wrong_code", "კოდი არასწორია", "Wrong code"));
      }
    } catch {
      setOtpVerified(false);
      setOtpMsg(TT("address.network_error", "ქსელური შეცდომა. სცადეთ ხელახლა.", "Network error. Try again."));
    } finally {
      setOtpSending(false);
    }
  };

  const resendOtp = async () => {
    if (otpTimer > 0 || otpSending) return;
    await sendOtp();
  };

  const ensurePhoneIfNeeded = async (digits) => {
    if (requirePhone !== true) return { ok: true };

    try {
      const payload = {
        user_id: resolvedUserId,
        phone: digits,
        phone_country_iso: country.iso,
        phone_country_code: country.dial,
      };

      const res = await api.post(`${API_BASE_URL}/ensure_phone.php`, payload, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      const data = res.data || {};
      if (data.success) return { ok: true };

      if (data.error === "phone_taken") {
        setPhoneErr(TT("address.phone_already_set", "ეს ნომერი უკვე გამოიყენება", "Phone already used"));
        return { ok: false };
      }

      setPhoneErr(TT("address.phone_save_failed", "ვერ მოხერხდა ტელეფონის შენახვა", "Failed to save phone"));
      return { ok: false };
    } catch {
      setPhoneErr(TT("address.network_error", "ქსელური შეცდომა. სცადეთ ხელახლა.", "Network error. Try again."));
      return { ok: false };
    }
  };

  const handleSubmit = async () => {
    // 🔒 double click guard
    if (paying) return;

    setPhoneErr("");

    if (!city?.trim() || !address?.trim()) {
      setPhoneErr(TT("address.fill_required", "გთხოვთ, შეავსოთ სავალდებულო ველები", "Please fill required fields"));
      return;
    }

    let phoneDigits = "";
    if (requirePhone === true) {
      phoneDigits = normalizeDigits(phone);
      if (!phoneDigits) {
        setPhoneErr(TT("address.phone_required", "ტელეფონის ნომერი სავალდებულოა", "Phone is required"));
        return;
      }
      if (!validByCountry(phoneDigits)) {
        setPhoneErr(
          (TT("address.phone_invalid_range", "ტელეფონის ფორმატი არასწორია", "Invalid phone format")) +
            ` (${country.min}-${country.max})`
        );
        return;
      }
      if (!otpVerified) {
        setPhoneErr(TT("address.verify_first", "გთხოვთ, ჯერ დაადასტუროთ ნომერი", "Please verify your phone first"));
        if (!otpVisible) await sendOtp();
        return;
      }
      const ensured = await ensurePhoneIfNeeded(phoneDigits);
      if (!ensured.ok) return;
    }

    // ✅ თუ აირჩია saved card -> save_card force 0
    const finalSaveCard = selectedCardToken ? 0 : (saveCard ? 1 : 0);

    const payload = {
      address,
      city_village: city,
      comment,

      phone: requirePhone === true ? phoneDigits : "",
      phone_country_iso: country.iso,
      phone_country_code: country.dial,
      phone_e164: requirePhone === true ? `+${country.dial}${phoneDigits}` : "",

      payment_method_token: selectedCardToken || "",
      save_card: finalSaveCard,

      // ✅ ძალიან მნიშვნელოვანია: start_payment.php იცოდეს ენა
      lang,
    };

    setPaying(true);
    try {
      const ok = await onSubmit(payload); // <-- შენი parent აქ აკეთებს start_payment call-ს და redirect-ს
      if (ok) onClose();
    } finally {
      setPaying(false);
    }
  };

  return (
    <Overlay role="dialog" aria-modal="true" aria-labelledby="addrTitle">
      <Popup>
        <ScrollArea>
          <Header>
            <div>
              <Title id="addrTitle">
                {TT("address.enter_title", "მისამართის შეყვანა", "Enter address")}
              </Title>
              <Subtitle>
                {TT("address.subtitle", "შეიყვანეთ მიწოდების მონაცემები", "Enter delivery details")}
              </Subtitle>
            </div>
            <CloseBtn
              aria-label={TT("address.close", "დახურვა", "Close")}
              onClick={onClose}
              disabled={paying}
              title={TT("address.close", "დახურვა", "Close")}
            >
              ×
            </CloseBtn>
          </Header>

          <Form>
            <Field>
              <Lab>{TT("address.city_label", "ქალაქი/სოფელი", "City/Village")}</Lab>
              <Select
                value={city}
                onChange={(e) => setCity(e.target.value)}
              >
                <option value="">
                  {TT("address.city_placeholder", "აირჩიეთ ქალაქი", "Choose city")}
                </option>
                <option value="თბილისი">
                  {lang === "ka" ? "თბილისი" : "Tbilisi"}
                </option>
              </Select>
              <WarningHint>
                {lang === "ka"
                  ? "მიწოდება ჯერჯერობით მხოლოდ თბილისის მასშტაბით ხორციელდება"
                  : "Delivery is currently available only within Tbilisi"}
              </WarningHint>
            </Field>

            <Field>
              <Lab>{TT("address.address_label", "მისამართი", "Address")}</Lab>
              <Textarea
                placeholder={TT("address.address_placeholder", "ქუჩა, კორპუსი, ბინა...", "Street, building, apt...")}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </Field>

            <Field>
              <Lab>{TT("address.comment_label", "კომენტარი (არასავალდებულო)", "Comment (optional)")}</Lab>
              <Textarea
                placeholder={TT("address.comment_placeholder", "შენიშვნა კურიერისთვის...", "Note for courier...")}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </Field>

            {requirePhone === true && (
              <>
                <PhoneRow>
                  <div>
                    <Field>
                      <Lab>{TT("address.country_label", "ქვეყანა", "Country")}</Lab>
                      <Select
                        value={phoneIso}
                        onChange={(e) => {
                          setPhoneIso(e.target.value);
                          setPhoneErr("");
                        }}
                      >
                        {COUNTRIES.map((cc) => (
                          <option key={cc.iso} value={cc.iso}>
                            {cc.name}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>

                  <div>
                    <Field>
                      <Lab>{TT("address.phone_label", "ტელეფონის ნომერი", "Phone")}</Lab>
                      <Input
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        placeholder={TT("address.phone_placeholder", `+${country.dial}`, `+${country.dial}`)}
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value.replace(/\D+/g, ""));
                          setPhoneErr("");
                        }}
                        style={{ borderColor: phoneErr ? PALETTE.danger : undefined }}
                      />
                      {phoneErr && <ErrorText>{phoneErr}</ErrorText>}
                    </Field>
                  </div>
                </PhoneRow>

                {!otpVerified ? (
                  <>
                    <Row>
                      <Button onClick={sendOtp} disabled={otpSending || paying}>
                        {otpSending
                          ? TT("address.sending", "იგზავნება...", "Sending...")
                          : TT("address.verify_phone", "ტელეფონის ვერიფიკაცია", "Verify phone")}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setOtpVisible(false);
                          setOtpMsg("");
                          setOtpCode("");
                        }}
                        disabled={paying}
                      >
                        {TT("address.clear", "გასუფთავება", "Clear")}
                      </Button>
                    </Row>

                    {otpVisible && (
                      <>
                        <Field>
                          <Lab>{TT("address.code_label", "კოდი (6 ციფრი)", "Code (6 digits)")}</Lab>
                          <Input
                            inputMode="numeric"
                            pattern="\d*"
                            maxLength={6}
                            placeholder={TT("address.code_placeholder", "123456", "123456")}
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                          />
                        </Field>

                        <Row>
                          <Button onClick={verifyOtp} disabled={otpSending || otpCode.length !== 6 || paying}>
                            {otpSending
                              ? TT("address.processing", "მუშავდება...", "Processing...")
                              : TT("address.verify", "დადასტურება", "Verify")}
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={resendOtp}
                            disabled={otpTimer > 0 || otpSending || paying}
                          >
                            {TT("address.resend", "ხელახლა გაგზავნა", "Resend")}
                            {otpTimer > 0 ? ` (${otpTimer}s)` : ""}
                          </Button>
                          <Button
                            variant="cancel"
                            onClick={() => {
                              setOtpVisible(false);
                              setOtpCode("");
                              setOtpMsg("");
                            }}
                            disabled={paying}
                          >
                            {TT("address.cancel", "გაუქმება", "Cancel")}
                          </Button>
                        </Row>

                        {otpMsg && (
                          <Hint style={{ color: otpVerified ? PALETTE.successInk : PALETTE.dim }}>
                            {otpMsg}
                          </Hint>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <Row>
                    <Badge>✔ {TT("address.verified", "დადასტურებულია", "Verified")}</Badge>
                  </Row>
                )}
              </>
            )}

            {/* ===== Saved cards / Save card ===== */}
            <PayBox>
              <PayTitle>{TT("payment.card_section_title", "გადახდა", "Payment")}</PayTitle>

              {savedCards.length > 0 && (
                <Field>
                  <Lab>{TT("payment.choose_saved", "აირჩიე დამახსოვრებული ბარათი", "Choose saved card")}</Lab>
                  <Select
                    value={selectedCardToken}
                    onChange={(e) => {
                      setSelectedCardToken(e.target.value);
                      if (e.target.value) setSaveCard(false);
                    }}
                    disabled={cardsLoading || paying}
                  >
                    <option value="">{TT("payment.use_new_card", "ახალი ბარათით გადახდა", "Pay with new card")}</option>
                    {savedCards.map((c) => (
                      <option key={c.token} value={c.token}>
                        {(c.brand || "CARD")} •••• {c.last4 || "----"}
                        {c.exp_month ? ` (${String(c.exp_month).padStart(2, "0")}/${c.exp_year || ""})` : ""}
                        {c.is_default ? ` — ${TT("payment.default", "Default", "Default")}` : ""}
                      </option>
                    ))}
                  </Select>

                  {!!cardsErr && <ErrorText>{cardsErr}</ErrorText>}
                  {cardsLoading && <Hint>{TT("payment.loading_cards", "ბარათები იტვირთება...", "Loading cards...")}</Hint>}
                </Field>
              )}

              <CheckRow>
                <Checkbox
                  type="checkbox"
                  checked={saveCard}
                  onChange={(e) => {
                    const v = e.target.checked;
                    setSaveCard(v);
                    if (v) setSelectedCardToken("");
                  }}
                  disabled={!!selectedCardToken || paying}
                />
                <span>{TT("payment.save_card", "დაიმახსოვრე ბარათი", "Save card")}</span>
              </CheckRow>

              <SmallMuted>
                {TT(
                  "payment.save_hint",
                  "ბარათის დეტალები ჩვენთან არ ინახება — მხოლოდ უსაფრთხო ტოკენი. დამახსოვრება მოხდება წარმატებული გადახდის შემდეგ.",
                  "We don’t store card details — only a secure token. Saving happens after successful payment."
                )}
              </SmallMuted>
            </PayBox>

            {/* ✅ buttons fixed: ONE onClick only */}
            <Buttons>
              <Button onClick={handleSubmit} disabled={paying}>
                {paying ? TT("payment.sending", "იგზავნება...", "Sending...") : TT("payment.send", "გაგზავნა", "Submit")}
              </Button>
              <Button variant="cancel" onClick={onClose} disabled={paying}>
                {TT("address.cancel", "გაუქმება", "Cancel")}
              </Button>
            </Buttons>
          </Form>
        </ScrollArea>
      </Popup>
    </Overlay>
  );
}
